import React, {createContext, useCallback, useContext, useReducer} from 'react'
import {type ChatBskyActorDefs} from '@atproto/api'

export type ChatWindowEntry = {
  id: string
  convoId: string | null
  recipientDid: string
  recipientProfile?: ChatBskyActorDefs.ProfileViewBasic
  isMinimized: boolean
}

type ChatBubbleState = {
  isBubbleOpen: boolean
  openWindows: ChatWindowEntry[]
}

type ChatBubbleAction =
  | {type: 'TOGGLE_PANEL'}
  | {type: 'CLOSE_PANEL'}
  | {
      type: 'OPEN_CHAT'
      recipientDid: string
      recipientProfile?: ChatBskyActorDefs.ProfileViewBasic
      convoId?: string
    }
  | {type: 'CLOSE_CHAT'; windowId: string}
  | {type: 'MINIMIZE_CHAT'; windowId: string}
  | {type: 'RESTORE_CHAT'; windowId: string}
  | {type: 'SET_CONVO_ID'; windowId: string; convoId: string}

const MAX_OPEN_WINDOWS = 5

let nextWindowId = 0
function generateWindowId() {
  return `chat-window-${++nextWindowId}`
}

function chatBubbleReducer(
  state: ChatBubbleState,
  action: ChatBubbleAction,
): ChatBubbleState {
  switch (action.type) {
    case 'TOGGLE_PANEL':
      return {...state, isBubbleOpen: !state.isBubbleOpen}
    case 'CLOSE_PANEL':
      return {...state, isBubbleOpen: false}
    case 'OPEN_CHAT': {
      // If a window for this recipient is already open, restore it
      const existing = state.openWindows.find(
        w => w.recipientDid === action.recipientDid,
      )
      if (existing) {
        return {
          ...state,
          isBubbleOpen: false,
          openWindows: state.openWindows.map(w =>
            w.id === existing.id ? {...w, isMinimized: false} : w,
          ),
        }
      }

      const newWindow: ChatWindowEntry = {
        id: generateWindowId(),
        convoId: action.convoId ?? null,
        recipientDid: action.recipientDid,
        recipientProfile: action.recipientProfile,
        isMinimized: false,
      }

      let windows = [newWindow, ...state.openWindows]
      // Enforce max window limit - close the oldest (rightmost in the stack)
      if (windows.length > MAX_OPEN_WINDOWS) {
        windows = windows.slice(0, MAX_OPEN_WINDOWS)
      }

      return {
        ...state,
        isBubbleOpen: false,
        openWindows: windows,
      }
    }
    case 'CLOSE_CHAT':
      return {
        ...state,
        openWindows: state.openWindows.filter(w => w.id !== action.windowId),
      }
    case 'MINIMIZE_CHAT':
      return {
        ...state,
        openWindows: state.openWindows.map(w =>
          w.id === action.windowId ? {...w, isMinimized: true} : w,
        ),
      }
    case 'RESTORE_CHAT':
      return {
        ...state,
        openWindows: state.openWindows.map(w =>
          w.id === action.windowId ? {...w, isMinimized: false} : w,
        ),
      }
    case 'SET_CONVO_ID':
      return {
        ...state,
        openWindows: state.openWindows.map(w =>
          w.id === action.windowId ? {...w, convoId: action.convoId} : w,
        ),
      }
    default:
      return state
  }
}

const initialState: ChatBubbleState = {
  isBubbleOpen: false,
  openWindows: [],
}

const ChatBubbleStateContext = createContext<ChatBubbleState>(initialState)
const ChatBubbleDispatchContext = createContext<
  React.Dispatch<ChatBubbleAction>
>(() => {})

export function ChatBubbleProvider({children}: {children: React.ReactNode}) {
  const [state, dispatch] = useReducer(chatBubbleReducer, initialState)

  return (
    <ChatBubbleStateContext.Provider value={state}>
      <ChatBubbleDispatchContext.Provider value={dispatch}>
        {children}
      </ChatBubbleDispatchContext.Provider>
    </ChatBubbleStateContext.Provider>
  )
}

export function useChatBubble() {
  return useContext(ChatBubbleStateContext)
}

export function useChatBubbleDispatch() {
  return useContext(ChatBubbleDispatchContext)
}

export function useOpenChat() {
  const dispatch = useChatBubbleDispatch()
  return useCallback(
    (
      recipientDid: string,
      recipientProfile?: ChatBskyActorDefs.ProfileViewBasic,
      convoId?: string,
    ) => {
      dispatch({type: 'OPEN_CHAT', recipientDid, recipientProfile, convoId})
    },
    [dispatch],
  )
}
